import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import XMarkIcon from './icons/XMarkIcon';

import { useTranslation } from 'react-i18next';
interface ImageWithModalProps {
  src: string;
  alt: string;
  className?: string;
}

const ImageWithModal: React.FC<ImageWithModalProps> = ({ src, alt, className }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const triggerRef = useRef<HTMLImageElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const openModal = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLImageElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openModal(e);
    }
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      const previouslyFocusedElement = document.activeElement as HTMLElement;

      // Use a timeout to ensure the element is in the DOM and focusable
      setTimeout(() => closeButtonRef.current?.focus(), 0);

      const handleFocusTrap = (event: KeyboardEvent) => {
        if (event.key === 'Tab' && modalRef.current) {
          const focusableElements = Array.from(
            modalRef.current.querySelectorAll<HTMLElement>(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter(el => el.offsetParent !== null); // only visible elements

          if (focusableElements.length === 0) return;

          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey) { // Shift + Tab
            if (document.activeElement === firstElement) {
              lastElement.focus();
              event.preventDefault();
            }
          } else { // Tab
            if (document.activeElement === lastElement) {
              firstElement.focus();
              event.preventDefault();
            }
          }
        }
      };

      const handleEsc = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeModal();
        }
      };

      document.addEventListener('keydown', handleFocusTrap);
      document.addEventListener('keydown', handleEsc);

      // Hide background content from screen readers. Assumes a single app root element with id="root".
      const appRoot = document.getElementById('root');
      if (appRoot) {
        appRoot.setAttribute('aria-hidden', 'true');
      }

      return () => {
        document.removeEventListener('keydown', handleFocusTrap);
        document.removeEventListener('keydown', handleEsc);
        if (appRoot) {
          appRoot.removeAttribute('aria-hidden');
        }
        // Return focus to the element that opened the modal
        previouslyFocusedElement?.focus();
      };
    }
  }, [isModalOpen, closeModal]);

  const modalContent = (
    <div ref={modalRef} className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center p-4" onClick={closeModal} aria-modal="true" role="dialog" aria-labelledby="image-modal-title">
      <div className="relative bg-white p-2 rounded-lg shadow-xl max-w-[95vw] max-h-[95vh]" onClick={e => e.stopPropagation()}>
        <h2 id="image-modal-title" className="sr-only">{t('imageWithModal.title')}</h2>
        <button ref={closeButtonRef} onClick={closeModal} className="absolute -top-3 -right-3 bg-white rounded-full p-1 text-slate-700 hover:text-black focus:outline-none focus:ring-2 focus:ring-sky-500" aria-label={t('imageWithModal.closeButtonLabel')}>
          <XMarkIcon className="w-8 h-8" />
        </button>
        <img src={src} alt={alt} className="object-contain max-w-[calc(95vw-1rem)] max-h-[calc(95vh-1rem)]" />
      </div>
    </div>
  );

  return (
    <>
      <img ref={triggerRef} src={src} alt={alt} className={`${className} cursor-pointer`} onClick={openModal} onKeyDown={handleTriggerKeyDown} role="button" tabIndex={0} />
      {isModalOpen && ReactDOM.createPortal(modalContent, document.body)}
    </>
  );
};

export default ImageWithModal;